import React, { useEffect, useState } from "react";
import { CloudRain, AlertTriangle } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { LAT, LON } from "@/components/WeatherWidget";

const URL = `${process.env.REACT_APP_BACKEND_URL}/api/weather?latitude=${LAT}&longitude=${LON}&daily=precipitation_sum,precipitation_probability_max,weather_code&past_days=1&forecast_days=2&timezone=Europe%2FRome`;
const HEAVY_MM = 10;          // abundant rain in a day
const LIKELY_MM = 5;          // moderate rain but very likely
const LIKELY_PROB = 70;
const HEAVY_CODES = new Set([65, 67, 82, 95, 96, 99]);

/* Returns null when the forecast is dry; otherwise which day triggers the alert. */
export function wetTrailsSignal(daily) {
    if (!daily?.time) return null;
    const labels = ["yesterday", "today", "tomorrow"];
    let hit = null;
    daily.time.forEach((_, i) => {
        const mm = daily.precipitation_sum?.[i] ?? 0;
        const prob = daily.precipitation_probability_max?.[i] ?? 0;
        const code = daily.weather_code?.[i] ?? 0;
        const wet = mm >= HEAVY_MM || (mm >= LIKELY_MM && prob >= LIKELY_PROB) || HEAVY_CODES.has(code);
        if (wet && (!hit || mm > hit.mm)) hit = { when: labels[i] || "today", mm: Math.round(mm), prob };
    });
    return hit;
}

const COPY = {
    it: {
        title: "TRAIL BAGNATI",
        yesterday: (mm) => `Ieri sono caduti circa ${mm} mm di pioggia: il fondo è ancora bagnato.`,
        today: (mm) => `Le previsioni danno pioggia abbondante oggi (circa ${mm} mm).`,
        tomorrow: (mm) => `Le previsioni danno pioggia abbondante domani (circa ${mm} mm).`,
        body: "Rocce e radici scivolose, fango nelle compressioni, frenate più lunghe. Riduci la velocità, stai attento e verifica le condizioni prima di partire.",
    },
    en: {
        title: "WET TRAILS",
        yesterday: (mm) => `About ${mm} mm of rain fell yesterday: the ground is still wet.`,
        today: (mm) => `Heavy rain forecast today (about ${mm} mm).`,
        tomorrow: (mm) => `Heavy rain forecast tomorrow (about ${mm} mm).`,
        body: "Slippery rocks and roots, mud in the compressions, longer braking. Slow down, ride carefully and check conditions before heading out.",
    },
};

/* Shown ONLY when the Argentario forecast (yesterday / today / tomorrow) brings abundant rain. */
export default function WetTrailsAlert({ compact = false, className = "" }) {
    const { lang } = useLang();
    const [signal, setSignal] = useState(null);

    useEffect(() => {
        let alive = true;
        fetch(URL).then((r) => (r.ok ? r.json() : null)).then((j) => { if (alive) setSignal(wetTrailsSignal(j?.daily)); }).catch(() => {});
        return () => { alive = false; };
    }, []);

    if (!signal) return null;
    const c = COPY[lang === "en" ? "en" : "it"];
    const lead = c[signal.when](signal.mm);

    return (
        <div data-testid="wet-trails-alert" role="status"
            className={`border border-abnormal-warn/60 bg-abnormal-warn/10 ${compact ? "p-3 md:p-4" : "p-4 md:p-5"} flex gap-3 md:gap-4 items-start ${className}`}>
            <div className="shrink-0 w-10 h-10 border border-abnormal-warn/60 flex items-center justify-center text-abnormal-warn">
                <CloudRain size={20} />
            </div>
            <div className="min-w-0">
                <p className="font-display uppercase text-abnormal-warn text-xl md:text-2xl leading-none inline-flex items-center gap-2">
                    <AlertTriangle size={14} /> {c.title}
                </p>
                <p className="mt-1.5 text-abnormal-paper text-sm md:text-base leading-snug">
                    <span className="font-semibold">{lead}</span>{!compact && <> {c.body}</>}
                </p>
                {compact && <p className="mt-1 text-abnormal-paperDim text-xs leading-snug">{c.body}</p>}
            </div>
        </div>
    );
}
